/**
 * DraggableStatsGrid · v2.7.15
 * ─────────────────────────────
 * Dashboard-widget shell with:
 *   1. Native HTML5 drag-and-drop reordering
 *   2. Show / hide per-widget toggles
 *   3. Per-widget size selector (S / M / L / XL)
 * All three pieces persist independently in localStorage so the user's
 * layout sticks across reloads.
 *
 * localStorage keys (under the supplied `storageKey`):
 *   • <storageKey>      → array<string>  · widget order
 *   • <storageKey>:vis  → {[id]: bool}   · show/hide
 *   • <storageKey>:size → {[id]: "sm"|"md"|"lg"|"xl"}
 *
 * Usage stays identical:
 *   <DraggableStatsGrid storageKey="admin-stats-order">
 *     <DraggableWidget id="drivers"><StatBox ... /></DraggableWidget>
 *     ...
 *   </DraggableStatsGrid>
 *
 * Optional per-widget `label` prop on <DraggableWidget> shows up in the
 * Customize popover; falls back to a Title-Case version of the id.
 */
import { Children, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GripVertical, RotateCcw, Settings2, Eye, EyeOff } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

// ---------- localStorage helpers ---------------------------------------------
const _safeGet = (k, fallback) => {
  try {
    if (typeof window === "undefined") return fallback;
    const raw = window.localStorage.getItem(k);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
};
const _safeSet = (k, v) => {
  try { window.localStorage.setItem(k, JSON.stringify(v)); } catch { /* quota / private */ }
};

// ---------- size map (responsive, 12-col base · 24-col on 2xl+) ------------
// v2.7.17 · 2xl variants let the grid widen to 24 cols on full-screen
// admin monitors so each "M" tile = 1/6 of the row instead of 1/4.
const SIZE_CLASS = {
  sm: "col-span-1 sm:col-span-1 lg:col-span-2 2xl:col-span-3",   // 1/8 row on 24-col
  md: "col-span-1 sm:col-span-2 lg:col-span-3 2xl:col-span-4",   // 1/6 row
  lg: "col-span-2 sm:col-span-2 lg:col-span-4 2xl:col-span-6",   // 1/4 row
  xl: "col-span-2 sm:col-span-4 lg:col-span-6 2xl:col-span-8",   // 1/3 row
};
// v2.7.18 · UNIFIED widget height standard. Every widget at a given tier
// renders at exactly the same height regardless of its internal content,
// so the grid stays geometrically symmetrical when users mix-and-match
// sizes. Heights step on a 32-px scale (8-pt grid · 4× multiplier).
const SIZE_HEIGHT_CLASS = {
  sm: "h-28",   // 112px
  md: "h-36",   // 144px
  lg: "h-44",   // 176px
  xl: "h-56",   // 224px
};
// Padding presets — kept identical across tiers so visual rhythm matches.
const CELL_PADDING = "p-0";  // outer wrapper has no padding — child Card carries it
const SIZE_LABELS = { sm: "S", md: "M", lg: "L", xl: "XL" };
const SIZE_ORDER  = ["sm", "md", "lg", "xl"];

const _titleCase = (id) => id.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// =============================================================================
export function DraggableWidget({ id, label, children }) {
  // label is consumed by the parent grid (read via React.Children.toArray)
  return <div data-widget-id={id} data-widget-label={label || _titleCase(id)} data-testid={`stats-widget-${id}`}>{children}</div>;
}

// =============================================================================
export default function DraggableStatsGrid({ storageKey, children, className = "" }) {
  const VIS_KEY  = `${storageKey}:vis`;
  const SIZE_KEY = `${storageKey}:size`;

  // ---- Declared metadata extracted from children -----------------------------
  const declaredMeta = useMemo(() => {
    const out = [];
    Children.forEach(children, (c) => {
      if (!isValidElement(c) || !c.props?.id) return;
      out.push({ id: c.props.id, label: c.props.label || _titleCase(c.props.id) });
    });
    return out;
  }, [children]);
  const declared    = useMemo(() => declaredMeta.map((m) => m.id), [declaredMeta]);
  const declaredSet = useMemo(() => new Set(declared), [declared]);

  // ---- Persistent state -----------------------------------------------------
  const [order, setOrder] = useState(() => {
    const saved = _safeGet(storageKey, []);
    const kept = (Array.isArray(saved) ? saved : []).filter((id) => declaredSet.has(id));
    const missing = declared.filter((id) => !kept.includes(id));
    return [...kept, ...missing];
  });
  const [visible, setVisible] = useState(() => {
    const saved = _safeGet(VIS_KEY, {});
    const map = {};
    declared.forEach((id) => { map[id] = saved[id] !== false; });   // default ON
    return map;
  });
  const [sizes, setSizes] = useState(() => {
    const saved = _safeGet(SIZE_KEY, {});
    const map = {};
    declared.forEach((id) => { map[id] = SIZE_ORDER.includes(saved[id]) ? saved[id] : "md"; });
    return map;
  });

  // Re-sync when the declared set changes (role-gated tiles add/remove).
  useEffect(() => {
    setOrder((prev) => {
      const kept = prev.filter((id) => declaredSet.has(id));
      const missing = declared.filter((id) => !kept.includes(id));
      const next = [...kept, ...missing];
      return next.length === prev.length && next.every((v, i) => v === prev[i]) ? prev : next;
    });
    setVisible((prev) => {
      const next = {};
      declared.forEach((id) => { next[id] = prev[id] !== false; });
      return next;
    });
    setSizes((prev) => {
      const next = {};
      declared.forEach((id) => { next[id] = SIZE_ORDER.includes(prev[id]) ? prev[id] : "md"; });
      return next;
    });
  }, [declared, declaredSet]);

  // v2.7.17 · React to server-side layout hydration so a fresh browser
  // picks up the operator's saved order / visibility / sizes from server.
  useEffect(() => {
    const onHydrated = (e) => {
      const incoming = e?.detail || {};
      if (Array.isArray(incoming.order))
        setOrder(incoming.order.filter((id) => declaredSet.has(id)));
      if (incoming.visibility && typeof incoming.visibility === "object")
        setVisible((prev) => ({ ...prev, ...incoming.visibility }));
      if (incoming.sizes && typeof incoming.sizes === "object") {
        const filtered = {};
        Object.entries(incoming.sizes).forEach(([k, v]) => {
          if (SIZE_ORDER.includes(v)) filtered[k] = v;
        });
        setSizes((prev) => ({ ...prev, ...filtered }));
      }
    };
    window.addEventListener("widget-layout:hydrated", onHydrated);
    return () => window.removeEventListener("widget-layout:hydrated", onHydrated);
  }, [declaredSet]);

  // Persist on every mutation.
  // v2.7.17 · Also broadcast a CustomEvent so `useWidgetLayoutSync` can
  // debounce a push to the server. The native `storage` event doesn't
  // fire for same-tab writes, hence the manual broadcast.
  const _emitChange = useCallback(() => {
    try { window.dispatchEvent(new CustomEvent("widget-layout:changed")); } catch { /* noop */ }
  }, []);
  useEffect(() => { _safeSet(storageKey, order);    _emitChange(); }, [storageKey, order, _emitChange]);
  useEffect(() => { _safeSet(VIS_KEY,    visible);  _emitChange(); }, [VIS_KEY, visible, _emitChange]);
  useEffect(() => { _safeSet(SIZE_KEY,   sizes);    _emitChange(); }, [SIZE_KEY, sizes, _emitChange]);

  // ---- Child lookup ---------------------------------------------------------
  const byId = useMemo(() => {
    const out = {};
    Children.forEach(children, (c) => {
      if (isValidElement(c) && c.props?.id) out[c.props.id] = c;
    });
    return out;
  }, [children]);

  // ---- Drag state -----------------------------------------------------------
  const dragId = useRef(null);
  const [overId, setOverId] = useState(null);

  const onDragStart = useCallback((e, id) => {
    dragId.current = id;
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); }
    catch { /* noop */ }
  }, []);
  const onDragOver = useCallback((e, id) => {
    e.preventDefault();
    if (!dragId.current || dragId.current === id) return;
    setOverId(id);
  }, []);
  const onDragLeave = useCallback((e, id) => { if (overId === id) setOverId(null); void e; }, [overId]);
  const onDrop = useCallback((e, targetId) => {
    e.preventDefault();
    const src = dragId.current;
    dragId.current = null;
    setOverId(null);
    if (!src || src === targetId) return;
    setOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(src);
      const toIdx = next.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, src);
      return next;
    });
  }, []);
  const onDragEnd = useCallback(() => { dragId.current = null; setOverId(null); }, []);

  // ---- Controls -------------------------------------------------------------
  const resetAll = useCallback(() => {
    setOrder([...declared]);
    const allOn = {};
    declared.forEach((id) => { allOn[id] = true; });
    setVisible(allOn);
    const allMd = {};
    declared.forEach((id) => { allMd[id] = "md"; });
    setSizes(allMd);
  }, [declared]);

  const toggleVisible = useCallback((id) => {
    setVisible((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const setSize = useCallback((id, size) => {
    if (!SIZE_ORDER.includes(size)) return;
    setSizes((prev) => ({ ...prev, [id]: size }));
  }, []);

  // ---- Render ---------------------------------------------------------------
  const visibleCount = order.filter((id) => visible[id]).length;
  const hiddenCount = declared.length - visibleCount;

  return (
    <div className="space-y-1" data-testid="draggable-stats-wrap">
      <div
        className={`grid grid-cols-2 sm:grid-cols-6 lg:grid-cols-12 2xl:grid-cols-[repeat(24,minmax(0,1fr))] gap-2 sm:gap-3 ${className}`}
        data-testid="draggable-stats-grid"
      >
        {order.map((id) => {
          if (!visible[id]) return null;
          const node = byId[id];
          if (!node) return null;
          const sizeCls = SIZE_CLASS[sizes[id]] || SIZE_CLASS.md;
          const heightCls = SIZE_HEIGHT_CLASS[sizes[id]] || SIZE_HEIGHT_CLASS.md;
          const isOver = overId === id;
          return (
            <div
              key={id}
              draggable
              onDragStart={(e) => onDragStart(e, id)}
              onDragOver={(e) => onDragOver(e, id)}
              onDragLeave={(e) => onDragLeave(e, id)}
              onDrop={(e) => onDrop(e, id)}
              onDragEnd={onDragEnd}
              // v2.7.18 · Universal sizing — every tier locks both column-span
              // AND height so same-tier widgets render geometrically
              // identical. `[&>*]:h-full [&>*]:w-full` forces inner Cards
              // (StatBox, WidgetShell) to fill the cell regardless of their
              // own internal padding.
              className={`group relative cursor-grab active:cursor-grabbing transition-all ${sizeCls} ${heightCls} ${CELL_PADDING} [&>*]:h-full [&>*]:w-full ${isOver ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-[#0c0d10] scale-[1.02]" : ""}`}
              data-testid={`draggable-widget-${id}`}
              data-size={sizes[id]}
              title="Drag to rearrange · use Customize to resize / hide"
            >
              <div className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-60 transition-opacity pointer-events-none">
                <GripVertical className="h-3 w-3 text-zinc-400"/>
              </div>
              {/* size pill, bottom-right */}
              <div className="absolute bottom-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-80 transition-opacity">
                <div className="flex bg-zinc-950/80 border border-zinc-700 rounded text-[8px] font-extrabold tracking-wider">
                  {SIZE_ORDER.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSize(id, s); }}
                      className={`px-1.5 py-0.5 ${sizes[id] === s ? "bg-amber-400 text-zinc-950" : "text-zinc-400 hover:text-amber-300"}`}
                      data-testid={`widget-size-${id}-${s}`}
                      title={`Resize ${id} to ${s.toUpperCase()}`}
                    >
                      {SIZE_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>
              {node}
            </div>
          );
        })}
      </div>

      {/* Toolbar — Customize popover + Reset link */}
      <div className="flex items-center justify-end gap-3 pt-1">
        {hiddenCount > 0 && (
          <span className="text-[10px] text-amber-400/80 font-bold" data-testid="hidden-widgets-counter">
            <EyeOff className="inline h-3 w-3 mr-0.5"/>{hiddenCount} hidden
          </span>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 transition-colors flex items-center gap-1"
              data-testid="draggable-stats-customize"
              title="Customize visible widgets + sizes"
            >
              <Settings2 className="h-3 w-3"/>Customize
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="bg-zinc-950 border-amber-400/30 w-[320px] p-3"
            data-testid="customize-widgets-popover"
          >
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Visible · Size</div>
            <div className="space-y-2">
              {declaredMeta.map(({ id, label }) => (
                <div key={id} className="flex items-center gap-2 text-xs" data-testid={`customize-row-${id}`}>
                  <button
                    type="button"
                    onClick={() => toggleVisible(id)}
                    className={`flex items-center gap-1.5 flex-1 min-w-0 px-2 py-1 rounded transition-colors ${visible[id] ? "text-amber-300 hover:bg-amber-400/10" : "text-zinc-500 hover:bg-zinc-800 line-through"}`}
                    data-testid={`customize-toggle-${id}`}
                    title={visible[id] ? "Hide widget" : "Show widget"}
                  >
                    {visible[id] ? <Eye className="h-3 w-3 flex-shrink-0"/> : <EyeOff className="h-3 w-3 flex-shrink-0"/>}
                    <span className="font-bold truncate">{label}</span>
                  </button>
                  <div className="flex bg-zinc-900 border border-zinc-700 rounded text-[9px] font-extrabold flex-shrink-0">
                    {SIZE_ORDER.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSize(id, s)}
                        disabled={!visible[id]}
                        className={`px-1.5 py-0.5 transition-colors ${sizes[id] === s ? "bg-amber-400 text-zinc-950" : "text-zinc-400 hover:text-amber-300"} ${!visible[id] ? "opacity-30 cursor-not-allowed" : ""}`}
                        data-testid={`customize-size-${id}-${s}`}
                      >
                        {SIZE_LABELS[s]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="pt-3 mt-3 border-t border-zinc-800 flex items-center justify-end">
              <button
                type="button"
                onClick={resetAll}
                className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 transition-colors flex items-center gap-1"
                data-testid="customize-reset-all"
                title="Restore default order, sizes, and visibility"
              >
                <RotateCcw className="h-3 w-3"/>Reset all
              </button>
            </div>
          </PopoverContent>
        </Popover>
        <button
          type="button"
          onClick={resetAll}
          className="text-[9px] uppercase tracking-widest text-zinc-600 hover:text-amber-400 transition-colors flex items-center gap-1"
          data-testid="draggable-stats-reset"
          title="Reset widget order to default"
        >
          <RotateCcw className="h-2.5 w-2.5"/>Reset layout
        </button>
      </div>
    </div>
  );
}
